/**
 * Utilities for Optimal Posting Times widget.
 * Data shape: OptimalPostingTimesData = { matrix: number[7][24], samples: number[7][24], timezone: string }
 */

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const FULL_DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export const OPTIMAL_POSTING_WIDGET_TITLE = 'Posting Time Performance'
export const OPTIMAL_POSTING_WIDGET_SUBTITLE =
  'See when your posts were published. Darker times had more posts.'
export const OPTIMAL_POSTING_ENGAGEMENT_SUBTITLE =
  'See post engagement. Darker times performed better.'
export const OPTIMAL_POSTING_UNIFIED_SUBTITLE =
  'Posts published by day and hour. Darker times had more posts.'
export const OPTIMAL_POSTING_TIER_LEGEND_LABELS = { 1: 'Lower', 2: 'Moderate', 3: 'Higher' }
export const OPTIMAL_POSTING_PROPERTY_TIMEZONE = 'America/Chicago'
export const OPTIMAL_POSTING_INSIGHTS_PANEL_TITLE = 'Posting Time Insights'
export const OPTIMAL_POSTING_INSIGHTS_PANEL_SUBTITLE =
  'Notable times based on post volume'
export const OPTIMAL_POSTING_HIGHEST_PERFORMING_LABEL = 'Highest performing posting time'
export const OPTIMAL_POSTING_METRICS_HELP_SECTIONS = [
  {
    title: 'Performance vs. peak',
    description:
      'How this posting time compares with the highest-performing time in the current view.',
  },
  {
    title: 'Share of total engagement',
    description:
      'How much of this period\'s post engagement came from posts published at this time.',
  },
]

export function formatOptimalPostingTimezoneLabel(timezone = OPTIMAL_POSTING_PROPERTY_TIMEZONE) {
  try {
    const genericParts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortGeneric',
    }).formatToParts(new Date())
    const genericName = genericParts.find((part) => part.type === 'timeZoneName')?.value
    if (genericName && !/^GMT[+-]/i.test(genericName)) {
      return `Timezone: ${genericName}`
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(new Date())
    const abbr = parts.find((part) => part.type === 'timeZoneName')?.value
    if (abbr && !/^GMT[+-]/i.test(abbr)) {
      return `Timezone: ${abbr}`
    }
    return null
  } catch {
    return null
  }
}

export const OPTIMAL_POSTING_PERFORMANCE_LABELS = {
  1: 'Lower',
  2: 'Moderate',
  3: 'Higher',
}

export const OPTIMAL_POSTING_TIER_TEXT_COLORS = {
  1: '#4B6FCC',
  2: '#6EA8FE',
  3: '#1D4ED8',
}

const MIN_SAMPLES_CONFIDENCE = 5

/** Status labels by sample count: Limited Data (<5), Emerging Pattern (5-14), Strong Signal (>=15) */
export function getSignalStatus(samples) {
  const n = typeof samples === 'number' ? samples : 0
  if (n < 5) return 'Limited Data'
  if (n < 15) return 'Emerging Pattern'
  return 'Strong Signal'
}

/**
 * Get all valid slots with value and samples, sorted by value desc.
 * @param {number[][]} matrix
 * @param {number[][]} samples
 * @param {{ minSamples?: number }} opts
 * @returns {{ dayIndex: number, hour: number, value: number, samples: number }[]}
 */
function getAllValidSlots(matrix, samples, { minSamples = 1 } = {}) {
  if (!matrix || !samples || !Array.isArray(matrix) || !Array.isArray(samples)) return []
  const slots = []
  for (let d = 0; d < 7; d++) {
    const row = matrix?.[d]
    const sampRow = samples?.[d]
    if (!row || !Array.isArray(row)) continue
    for (let h = 0; h < 24; h++) {
      const val = typeof row[h] === 'number' ? row[h] : 0
      const samp = (sampRow && typeof sampRow[h] === 'number') ? sampRow[h] : 0
      if (samp >= minSamples) {
        slots.push({ dayIndex: d, hour: h, value: val, samples: samp })
      }
    }
  }
  slots.sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
  return slots
}

/**
 * Valid slots for any rectangular heatmap (e.g. trimmed hour rows x calendar days).
 */
function getAllValidSlotsGrid(matrix, samples, { minSamples = 1 } = {}) {
  if (!matrix || !samples || !Array.isArray(matrix) || !Array.isArray(samples)) return []
  const slots = []
  for (let r = 0; r < matrix.length; r += 1) {
    const row = matrix[r]
    const sampRow = samples[r]
    if (!row || !Array.isArray(row)) continue
    for (let c = 0; c < row.length; c += 1) {
      const val = typeof row[c] === 'number' ? row[c] : 0
      const samp = sampRow && typeof sampRow[c] === 'number' ? sampRow[c] : 0
      if (samp >= minSamples) {
        slots.push({ row: r, col: c, value: val, samples: samp })
      }
    }
  }
  slots.sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
  return slots
}

/**
 * Get top N time slots by matrix value with minimum sample count.
 * Includes percentile rank and lowConfidence flag.
 * @param {number[][]} matrix - 7x24 engagement matrix (matrix[d][h])
 * @param {number[][]} samples - 7x24 sample counts
 * @param {{ minSamples?: number, topN?: number }} opts
 * @returns {{ dayIndex: number, hour: number, value: number, samples: number, percentile: number, lowConfidence: boolean }[]}
 */
export function getTopSlots(matrix, samples, { minSamples = 1, topN = 2 } = {}) {
  const all = getAllValidSlots(matrix, samples, { minSamples })
  const totalSlots = all.length
  return all.slice(0, topN).map((slot, i) => {
    const percentile = totalSlots > 0 ? Math.round(((totalSlots - i) / totalSlots) * 100) : 0
    const lowConfidence = slot.samples < MIN_SAMPLES_CONFIDENCE
    return {
      ...slot,
      percentile: Math.min(100, Math.max(1, percentile)),
      lowConfidence,
      rank: i + 1,
      totalSlots,
    }
  })
}

/**
 * Get percentile rank for a cell (1-100, higher = better).
 * @param {number} dayIndex
 * @param {number} hour
 * @param {number[][]} matrix
 * @param {number[][]} samples
 * @param {{ minSamples?: number }} opts
 * @returns {{ percentile: number, lowConfidence: boolean } | null}
 */
export function getCellRank(dayIndex, hour, matrix, samples, { minSamples = 1 } = {}) {
  const all = getAllValidSlots(matrix, samples, { minSamples })
  const idx = all.findIndex((s) => s.dayIndex === dayIndex && s.hour === hour)
  if (idx === -1) return null
  const total = all.length
  const percentile = total > 0 ? Math.round(((total - idx) / total) * 100) : 0
  const samp = samples?.[dayIndex]?.[hour] ?? 0
  return {
    percentile: Math.min(100, Math.max(1, percentile)),
    lowConfidence: samp < MIN_SAMPLES_CONFIDENCE,
  }
}

/**
 * Generate pattern interpretation from top slots (clustering logic).
 * @param {{ dayIndex: number, hour: number }[]} topSlots
 * @returns {string}
 */
export function getPatternInterpretation(topSlots) {
  if (!topSlots || topSlots.length === 0) return ''
  const weekday = [0, 1, 2, 3, 4]
  const weekend = [5, 6]
  const morning = (h) => h >= 6 && h <= 9
  const evening = (h) => h >= 18 && h <= 21
  const afternoon = (h) => h >= 12 && h <= 16

  const wkdayMorn = topSlots.filter((s) => weekday.includes(s.dayIndex) && morning(s.hour))
  const wkdayEve = topSlots.filter((s) => weekday.includes(s.dayIndex) && evening(s.hour))
  const wkendMorn = topSlots.filter((s) => weekend.includes(s.dayIndex) && morning(s.hour))
  const wkendEve = topSlots.filter((s) => weekend.includes(s.dayIndex) && evening(s.hour))
  const wkendAft = topSlots.filter((s) => weekend.includes(s.dayIndex) && afternoon(s.hour))

  if (wkdayMorn.length >= 2 && wkdayEve.length === 0) {
    return 'Engagement consistently peaks on weekday mornings (7-9 AM).'
  }
  if (wkdayEve.length >= 2 && wkdayMorn.length === 0) {
    return 'Engagement consistently peaks on weekday evenings (6-9 PM).'
  }
  if (wkendEve.length >= 2) {
    return 'Engagement peaks on weekend evenings.'
  }
  if (wkendMorn.length >= 1 && wkdayMorn.length >= 1) {
    return 'Engagement peaks on weekday and weekend mornings.'
  }
  if (wkdayMorn.length >= 1 && wkdayEve.length >= 1) {
    return 'Engagement consistently peaks on weekday mornings (7-9 AM) and evenings (6-9 PM).'
  }
  if (wkendEve.length >= 1 || wkendAft.length >= 1) {
    return 'Engagement peaks on weekend afternoons and evenings.'
  }
  return 'Engagement varies by day and time - post during your top recommended slots for best results.'
}

/**
 * Format a slot for display.
 * @param {number} dayIndex - 0=Mon, 6=Sun
 * @param {number} hour - 0-23
 * @param {string} [timezone='UTC']
 * @returns {string} e.g. "Tue, 7:00 PM"
 */
export function formatSlot(dayIndex, hour, timezone = 'UTC') {
  const dayName = DAY_NAMES[dayIndex] ?? 'Day'
  const h = Math.max(0, Math.min(23, Math.floor(hour)))
  const isPM = h >= 12
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  const ampm = isPM ? 'PM' : 'AM'
  return `${dayName}, ${displayHour}:00 ${ampm}`
}

/**
 * Format slot for signal block: "Wed - 8:00 AM"
 */
export function formatSlotShort(dayIndex, hour) {
  const dayName = DAY_NAMES[dayIndex] ?? 'Day'
  const h = Math.max(0, Math.min(23, Math.floor(hour)))
  const isPM = h >= 12
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  const ampm = isPM ? 'PM' : 'AM'
  return `${dayName} - ${displayHour}:00 ${ampm}`
}

/**
 * Tooltip header for weekly heatmap: "Thursday - 12:00 PM"
 */
export function formatSlotTooltipHeader(dayIndex, hour) {
  const dayName = FULL_DAY_NAMES[dayIndex] ?? 'Day'
  const h = Math.max(0, Math.min(23, Math.floor(hour)))
  const isPM = h >= 12
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  const ampm = isPM ? 'PM' : 'AM'
  return `${dayName} - ${displayHour}:00 ${ampm}`
}

/**
 * @param {number} count
 * @returns {string} e.g. "1 Post Published" / "2 Posts Published"
 */
export function formatPostsPublishedLabel(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0))
  return n === 1 ? '1 Post Published' : `${n} Posts Published`
}

/**
 * @param {number} count
 * @returns {string} e.g. "1 post published" / "2 posts published"
 */
export function formatPostsPublished(count) {
  const n = Math.max(0, Math.floor(Number(count) || 0))
  return n === 1 ? '1 post published' : `${n} posts published`
}

/**
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function formatPostingTimeMetricPercent(value) {
  if (value == null || !Number.isFinite(Number(value))) return 'Not available'
  const rounded = Math.round(Number(value) * 10) / 10
  const text = rounded % 1 === 0 ? String(rounded) : rounded.toFixed(1)
  return `${text}%`
}

export function formatPerformanceVsPeakCompact(value) {
  const formatted = formatPostingTimeMetricPercent(value)
  if (formatted === 'Not available') return formatted
  return `${formatted} of peak`
}

export function formatShareOfTotalEngagementCompact(value) {
  const formatted = formatPostingTimeMetricPercent(value)
  if (formatted === 'Not available') return formatted
  return `${formatted} of total engagement`
}

export function formatCombinedPeakAndShare(performanceVsPeak, shareOfTotalEngagement) {
  const peak = formatPerformanceVsPeakCompact(performanceVsPeak)
  const share = formatShareOfTotalEngagementCompact(shareOfTotalEngagement)
  if (peak === 'Not available' && share === 'Not available') return null
  if (peak === 'Not available') return share
  if (share === 'Not available') return peak
  return `${peak} - ${share}`
}

export const OPTIMAL_POSTING_SLOT_EMPTY_MESSAGE =
  'No publishing performance data available'

function collectValidMatrixCells(matrix, samples, { minSamples = 1 } = {}) {
  if (!matrix || !samples || !Array.isArray(matrix) || !Array.isArray(samples)) return []
  const cells = []
  for (let row = 0; row < matrix.length; row += 1) {
    const valueRow = matrix[row]
    const sampleRow = samples[row]
    if (!valueRow || !Array.isArray(valueRow)) continue
    for (let col = 0; col < valueRow.length; col += 1) {
      const posts = Number(sampleRow?.[col] ?? 0)
      if (posts < minSamples) continue
      const performance = Number(valueRow[col] ?? 0)
      cells.push({
        row,
        col,
        performance,
        posts,
        engagementWeight: performance * posts,
      })
    }
  }
  return cells
}

/**
 * Shared view context for posting-time performance metrics.
 */
export function buildPostingTimeViewContext(matrix, samples, { minSamples = 1 } = {}) {
  const cells = collectValidMatrixCells(matrix, samples, { minSamples })
  if (!cells.length) {
    return { peakPerformance: null, totalEngagementWeight: 0, totalPosts: 0, cells }
  }
  const peakPerformance = Math.max(...cells.map((cell) => cell.performance))
  const totalEngagementWeight = cells.reduce((sum, cell) => sum + cell.engagementWeight, 0)
  const totalPosts = cells.reduce((sum, cell) => sum + cell.posts, 0)
  return { peakPerformance, totalEngagementWeight, totalPosts, cells }
}

/**
 * Metrics for a single matrix cell (weekly day-hour or daily date-hour grid).
 */
function slotShareOfTotalEngagement(engagements, row, col, totalEngagements) {
  if (totalEngagements == null || !Number.isFinite(Number(totalEngagements)) || Number(totalEngagements) <= 0) {
    return null
  }
  const slotEngagement = engagements?.[row]?.[col]
  if (slotEngagement == null || !Number.isFinite(Number(slotEngagement))) return null
  return (Number(slotEngagement) / Number(totalEngagements)) * 100
}

export function getMatrixCellPostingTimeMetrics(matrix, samples, row, col, {
  minSamples = 1,
  engagements = null,
  totalEngagements = null,
} = {}) {
  const posts = Number(samples?.[row]?.[col] ?? 0)
  if (posts < minSamples) {
    return { hasValidPosts: false, postCount: posts }
  }

  const performance = Number(matrix?.[row]?.[col] ?? 0)
  const { peakPerformance, totalPosts } = buildPostingTimeViewContext(matrix, samples, { minSamples })
  if (peakPerformance == null || totalPosts <= 0) {
    return {
      hasValidPosts: true,
      postCount: posts,
      performanceVsPeak: null,
      shareOfTotalEngagement: null,
      isHighestPerforming: false,
    }
  }

  const isHighestPerforming = Math.abs(performance - peakPerformance) < 0.01
  const performanceVsPeak = isHighestPerforming
    ? null
    : peakPerformance > 0
      ? (performance / peakPerformance) * 100
      : null

  return {
    hasValidPosts: true,
    postCount: posts,
    performanceVsPeak,
    shareOfTotalEngagement: slotShareOfTotalEngagement(engagements, row, col, totalEngagements),
    isHighestPerforming,
  }
}

export function getWeekdaySlotPostingTimeMetrics(matrix, samples, dayIndex, hour, options = {}) {
  return getMatrixCellPostingTimeMetrics(matrix, samples, dayIndex, hour, options)
}

/**
 * @param {number} samples
 * @param {number} tierLevel
 * @param {number} [minSamples=1]
 */
export function hasSlotPerformanceData(samples, tierLevel, minSamples = 1) {
  const n = Number(samples) || 0
  return n >= minSamples && tierLevel >= 1 && tierLevel <= 3
}

/**
 * @param {HTMLElement} anchorEl
 * @returns {{ x: number, y: number, transform: string }}
 */
export function measureOptimalPostingTooltipLayout(anchorEl) {
  const r = anchorEl.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  const halfW = 110
  const approxH = 96
  const gutter = 10
  let x = r.left + r.width / 2
  x = Math.max(halfW + gutter, Math.min(vw - halfW - gutter, x))

  const spaceAbove = r.top
  const spaceBelow = vh - r.bottom
  const placeAbove = spaceAbove >= approxH + gutter || spaceAbove >= spaceBelow

  if (placeAbove) {
    return { x, y: r.top - 8, transform: 'translate(-50%, -100%)' }
  }
  return { x, y: r.bottom + 8, transform: 'translate(-50%, 0)' }
}

/**
 * Build recommendation text from top slots.
 * @param {{ dayIndex: number, hour: number }[]} topSlots
 * @param {string} [timezone='UTC']
 * @returns {string}
 */
export function getRecommendationText(topSlots, timezone = 'UTC') {
  if (!topSlots || topSlots.length === 0) {
    return 'Not enough data yet...'
  }
  const parts = topSlots.map((s) => formatSlot(s.dayIndex, s.hour, timezone))
  if (parts.length === 1) {
    return `Your audience is most engaged on ${parts[0]}.`
  }
  return `Your audience is most engaged on ${parts[0]} and ${parts[1]}.`
}

/**
 * Sum total posts used across the matrix.
 * @param {number[][]} samples
 * @returns {number}
 */
export function getTotalPostsUsed(samples) {
  if (!samples || !Array.isArray(samples)) return 0
  let total = 0
  for (const row of samples) {
    if (Array.isArray(row)) {
      for (const n of row) {
        if (typeof n === 'number') total += n
      }
    }
  }
  return total
}

/**
 * Get value thresholds for quartiles (for heatmap styling).
 * @param {number[][]} matrix
 * @param {number[][]} samples
 * @param {{ minSamples?: number }} opts
 * @returns {{ q1: number, q3: number, top3Keys: string[] }}
 */
export function getHeatmapQuartiles(matrix, samples, { minSamples = 1 } = {}) {
  const all = getAllValidSlots(matrix, samples, { minSamples })
  if (all.length === 0) return { q1: 0, q3: 0, top3Keys: [] }
  const values = all.map((s) => s.value).filter((v) => typeof v === 'number')
  if (values.length === 0) return { q1: 0, q3: 0, top3Keys: [] }
  values.sort((a, b) => a - b)
  const q1Idx = Math.floor(values.length * 0.25)
  const q3Idx = Math.floor(values.length * 0.75)
  const q1 = values[q1Idx] ?? 0
  const q3 = values[q3Idx] ?? 0
  const top3 = all.slice(0, 3)
  const top3Keys = top3.map((s) => `${s.dayIndex}-${s.hour}`)
  return { q1, q3, top3Keys }
}

/**
 * Quartile thresholds for an arbitrary rows-cols engagement matrix (e.g. date-range widget).
 * @param {number[][]} matrix
 * @param {number[][]} samples
 * @param {{ minSamples?: number }} [opts]
 * @returns {{ q1: number, q3: number, top3Keys: string[] }}
 */
export function getHeatmapQuartilesGrid(matrix, samples, { minSamples = 1 } = {}) {
  const all = getAllValidSlotsGrid(matrix, samples, { minSamples })
  if (all.length === 0) return { q1: 0, q3: 0, top3Keys: [] }
  const values = all.map((s) => s.value).filter((v) => typeof v === 'number')
  if (values.length === 0) return { q1: 0, q3: 0, top3Keys: [] }
  values.sort((a, b) => a - b)
  const q1Idx = Math.floor(values.length * 0.25)
  const q3Idx = Math.floor(values.length * 0.75)
  const q1 = values[q1Idx] ?? 0
  const q3 = values[q3Idx] ?? 0
  const top3 = all.slice(0, 3)
  const top3Keys = top3.map((s) => `${s.row}-${s.col}`)
  return { q1, q3, top3Keys }
}

/**
 * Aggregate engagement by hour across all days (weighted by samples).
 * @param {number[][]} matrix
 * @param {number[][]} samples
 * @returns {{ hour: number, value: number, samples: number }[]}
 */
export function getHourlyEngagement(matrix, samples) {
  if (!matrix || !samples) return []
  const result = []
  for (let h = 0; h < 24; h++) {
    let sum = 0
    let count = 0
    for (let d = 0; d < 7; d++) {
      const v = matrix?.[d]?.[h] ?? 0
      const s = samples?.[d]?.[h] ?? 0
      sum += v * s
      count += s
    }
    result.push({
      hour: h,
      value: count > 0 ? sum / count : 0,
      samples: count,
    })
  }
  return result
}

/**
 * Format hour for display (12 AM, 1 AM, ... 11 PM).
 */
export function formatHourLabel(hour) {
  const h = Math.max(0, Math.min(23, Math.floor(hour)))
  if (h === 0) return '12 AM'
  if (h === 12) return '12 PM'
  return h < 12 ? `${h} AM` : `${h - 12} PM`
}

export { DAY_NAMES, MIN_SAMPLES_CONFIDENCE }
